// Types
import type { User } from '@white-label-types/parking-shared';
import type { AxiosRequestConfig } from 'axios';
import { JSONApiResponse } from '@white-label-types/api';
import { Order } from '@white-label-types/account-booking';

// Parsers
import { parserGetUserData } from '@white-label-helper/get-user-data-parser';

// Helpers
import { pusherErrorTimeout } from '@white-label-configuration/constants';
import { httpBooking } from '@white-label-configuration/http';

import { myApp } from '@white-label-plugin/global-app-context';
import { datadogRum } from '@datadog/browser-rum';
import { isBookingPortalFlow } from '@white-label-helper/booking-portal-flow';

const SAGA_STATUS_POLL_INTERVAL = 3000 as const;

interface BookingApiResponse<TBody> {
  body: TBody;
}

const waitForNextSagaStatusPoll = (delay: number): Promise<void> => new Promise((resolve) => {
  setTimeout(resolve, delay);
});

const getSagaPollingTimeoutError = (): Error => {
  const ONE_SECOND = 1000;
  return new Error(`Timed out after ${pusherErrorTimeout / ONE_SECOND} seconds`);
};

const isSagaSuccessfulStatusResponse = (body: unknown): body is SagaSuccessfulStatusResponse => {
  return Boolean(
    body
    && typeof body === 'object'
    && 'management_token' in body
    && body.management_token
    && typeof body.management_token === 'object'
    && 'token' in body.management_token
  );
};

const isSagaFailedStatusResponse = (body: unknown): body is SagaFailedStatusResponse => {
  return Boolean((body as SagaFailedStatusResponse)?.data?.status === 'failed');
};

const pollBookingSagaStatus = async (
  sagaId: string,
  pollingStartedAt: number = Date.now(),
): Promise<SagaSuccessfulStatusResponse> => {
  if (Date.now() - pollingStartedAt >= pusherErrorTimeout) {
    throw getSagaPollingTimeoutError();
  }

  const response = await handleGetBookingSagaStatus(sagaId);
  const sagaStatusBody = response.body;

  if (isSagaSuccessfulStatusResponse(sagaStatusBody)) {
    return sagaStatusBody;
  }

  if (isSagaFailedStatusResponse(sagaStatusBody)) {
    throw sagaStatusBody;
  }

  const remainingTime = pusherErrorTimeout - (Date.now() - pollingStartedAt);
  await waitForNextSagaStatusPoll(Math.min(SAGA_STATUS_POLL_INTERVAL, remainingTime));

  return pollBookingSagaStatus(sagaId, pollingStartedAt);
};

const settleBookingWithoutPaymentSaga = async (
  request: Promise<BookingApiResponse<SagaIdResponse>>,
): Promise<SagaSuccessfulStatusResponse> => {
  const response = await request;
  const sagaId = response.body?.saga_id;

  if (!sagaId) {
    throw new Error('Missing saga_id in booking distribution response');
  }

  return pollBookingSagaStatus(sagaId);
};


// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getBookingsForUser = (bearerToken: string, queryParams?: Record<string, any>): Promise<JSONApiResponse<Order[]>> => {
  return httpBooking.get(
    `account/orders/`,
    {
      headers: { Authorization: bearerToken },
      query: queryParams,
    }
  )
    .then((res) => res.body)
};

// @ts-expect-error - error here
httpBooking.onRequest((config) => {
  if (isBookingPortalFlow()) {
    // @ts-expect-error - no param
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = `Bearer ${myApp.$auth?.user['https://user_metadata/channel_token']}`;
  }
  config.headers['X-Datadog-Session-ID'] = datadogRum.getInternalContext()?.session_id || null;
});

export interface BookingOrderBody {
  cart_token: string;
  user: User;
  gateway_data?: {
    payment_method_id: string;
    merchant_id: string;
  };
  booker_email?: string | null;
  customer_email?: string | null;
  others_email?: string | null;
  send_distribution_api_emails: boolean
}

export interface UpdateBookingOrderBody extends BookingOrderBody {
  original_order_item_id: number;
}

export interface ContinueBookingBody {
  status: string;
  forward: {
    saga_id: string;
    saga_message_id: string;
  }
}

interface DifferenceAfterAmendmentBody {
  originalOrderItemId: string;
  cartItemTotal: number;
  cartItemProductDeposit: number
}

export interface DifferenceAfterAmendmentResponse {
  amendment_fee: number;
  amount_paid_difference: number;
  original_order_item_total: number;
  cart_item_total: number;
  difference: number;
  original_order_item?: { [key: string]: unknown };
  original_order_item_product_deposit: number
}

export interface SagaSuccessfulStatusResponse {
  management_token: {
    token: string,
    expiration: number
  }
}
export interface SagaFailedStatusResponse {
  data: {
    status: 'failed',
  }
}
export interface SagaIdResponse {
  saga_id: string,
}
// @ts-expect-error property
httpBooking.onRequest((config) => {
  const session_id = datadogRum.getInternalContext()?.session_id || null;
  config.headers['X-Datadog-Session-ID'] = session_id;
});

export const handleBookingWithoutPayment = (body: BookingOrderBody): Promise<SagaSuccessfulStatusResponse> => (
  settleBookingWithoutPaymentSaga(
    httpBooking.post('booking/distribution/order', { body }) as Promise<BookingApiResponse<SagaIdResponse>>,
  )
);
export const handleAmendBookingWithoutPayment = (body: UpdateBookingOrderBody): Promise<SagaSuccessfulStatusResponse> => (
  settleBookingWithoutPaymentSaga(
    httpBooking.put('booking/distribution/order', { body }) as Promise<BookingApiResponse<SagaIdResponse>>,
  )
);
export const handleGetBookingSagaStatus = (
  sagaId: string,
): Promise<BookingApiResponse<SagaSuccessfulStatusResponse | SagaFailedStatusResponse>> => (
  httpBooking.get(`booking/status/${sagaId}`) as Promise<BookingApiResponse<SagaSuccessfulStatusResponse | SagaFailedStatusResponse>>
);

export const handlePaymentRequestOriginal = (body: BookingOrderBody): Promise<AxiosRequestConfig> => (
  httpBooking.post('booking/order', { body })
);

export const handlePaymentRequestManageBooking = (body: UpdateBookingOrderBody): Promise<AxiosRequestConfig> => (
  httpBooking.put('booking/order', { body })
);

export const handlePaymentRequestExtendedBooking = (body: UpdateBookingOrderBody): Promise<AxiosRequestConfig> => (
  httpBooking.put('booking/order/upgrade', { body })
);

export const continueBookingRequest = (body: ContinueBookingBody): Promise<AxiosRequestConfig> => (
  httpBooking.post('booking/order/continue', { body })
);

export const getUserData = (piiToken: string): Promise<ReturnType<typeof parserGetUserData>> => (
  httpBooking.get(`pii-data/${piiToken}`)
    .then(({ body }) => parserGetUserData(body.data))
);

export const differenceAfterAmendment = (
  manageBookingToken: string,
  { originalOrderItemId, cartItemTotal, cartItemProductDeposit }: DifferenceAfterAmendmentBody,
): Promise<DifferenceAfterAmendmentResponse> => (
  httpBooking.post('/booking/order/item/difference-after-amendment', {
    headers: { 'Management-Token': manageBookingToken },
    body: {
      original_order_item_id: originalOrderItemId,
      cart_item_total: cartItemTotal,
      cart_item_product_deposit: cartItemProductDeposit,
    },
  }).then((response) => response?.body)
);
